import { defineStore } from "pinia";
import { reactive, ref } from "vue";
import qs from "qs";
import { fetcher } from "vacv-components";
import { DateTime } from "luxon";
import { zeroPad } from "../utils/numbers";

const yyyy_mm_dd = "yyyy-MM-dd";

type YearMonth = {
  year: number;
  month: number;
};

type DisplayedMonths = {
  left: YearMonth;
  right: YearMonth;
};

export const useFlightAvailabilityStore = defineStore(
  "flightAvailability",
  () => {
    const VITE_FLIGHT_AVAILABILITY_URL = ref("");
    const DEBOUNCE_TIMEOUT = ref(0);

    /* 
        availableDates cache is a Map -> Map -> Set data structure
        
        "origin:destination": {
            "YYYY-MM": [ "01", "02", "03".... for the days of the month that have available flights ]
        }
    */
    const availableDates = ref<Map<string, Map<string, Set<string>>>>(
      new Map()
    );

    const hasUnavailableFlights = ref(false);
    const FETCH_TIMEOUT = ref(60000);

    const origin = ref<string>("");
    const destination = ref<string>("");

    const minDate = ref<string | undefined>();
    const maxDate = ref<string | undefined>();

    function setFlightTrip(_origin: string, _destination: string) {
      origin.value = _origin;
      destination.value = _destination;
    }

    // these are the two months of the Date Picker that are currently visible
    // the months that are visible drive the http requests to populate the cache
    // unfortunately it looks like there is no API vue3datepicker for determining these months
    // so some work was added to keep these in sync with the actual calendar
    const displayedMonths = reactive<DisplayedMonths>({
      left: {
        year: 0,
        month: 0,
      },
      right: {
        year: 0,
        month: 0,
      },
    });

    const fetchDebounceTimeout = ref();
    const lastFetch = ref(); // stores the time of the last request as reference for debouncing

    function reloadAvailabilityData(isDepartureSelected: boolean) {
      // we must force a reload of the data for the 2 visible months when switching focus from the
      // depature to return text fields, because the return dates might not be loaded
      updateDisplayedMonths(
        isDepartureSelected,
        displayedMonths.left.year,
        displayedMonths.left.month,
        displayedMonths.right.year,
        displayedMonths.right.month,
        false
      );
    }

    // after date change is detected, update the displayedMonths and call fetchFlightAvailability after debouncing
    function updateDisplayedMonths(
      isDepartureSelected: boolean,
      leftYear: number,
      leftMonth: number,
      rightYear: number,
      rightMonth: number,
      debounce: boolean
    ) {
      if (!VITE_FLIGHT_AVAILABILITY_URL.value) {
        // if the URL for flight availability requests is not set, then this feature is disabled
        return;
      }
      if (fetchDebounceTimeout.value) {
        clearTimeout(fetchDebounceTimeout.value);
      }

      displayedMonths.left.year = leftYear;
      displayedMonths.left.month = leftMonth;
      displayedMonths.right.year = rightYear;
      displayedMonths.right.month = rightMonth;

      let debounceTime = debounce ? DEBOUNCE_TIMEOUT.value : 0;

      if (debounce) {
        if (
          !lastFetch.value ||
          new Date().getTime() - lastFetch.value > DEBOUNCE_TIMEOUT.value
        ) {
          // if it's been more than the debounce time since the last fetch, then no need to debounce this time
          debounceTime = 0;
        }
      }

      lastFetch.value = new Date().getTime();

      fetchDebounceTimeout.value = setTimeout(() => {
        fetchFlightAvailability(isDepartureSelected);
      }, debounceTime);
    }

    function isMonthLoaded(_flightDirection: string, yearMonthStr: string) {
      const months = availableDates.value.get(_flightDirection);
      if (months?.get(yearMonthStr)) {
        return true;
      }
      return false;
    }

    function dateHasFlightAvailibility(
      flightDirection: string,
      dateStr: string
    ) {
      const monthMap = availableDates.value.get(flightDirection);
      if (monthMap && isDateStr(dateStr)) {
        const yearMonthStr = dateStr.substring(0, 7);
        const dayStr = dateStr.substring(8, 10);
        const daysSet = monthMap.get(yearMonthStr);
        if (daysSet) {
          return daysSet.has(dayStr);
        }
      }
      return false;
    }

    function getFlightDirection(isDepartureSelected: boolean) {
      return isDepartureSelected
        ? origin.value + ":" + destination.value
        : destination.value + ":" + origin.value;
    }

    async function fetchFlightAvailability(isDepartureSelected: boolean) {
      const monthsToLoad = [displayedMonths.left, displayedMonths.right];

      const filteredMonths = monthsToLoad.filter((yearmonth) => {
        // filter out months that were already loaded
        const yearMonthStr =
          yearmonth.year + "-" + zeroPad(yearmonth.month + 1);

        const monthData = availableDates.value.get(
          getFlightDirection(isDepartureSelected)
        );
        if (monthData) {
          if (monthData.get(yearMonthStr)) {
            // if the month is found in the "origin:dest" map, then return false so we don't fetch that month data again
            return false;
          }
        }
        return true;
      });

      if (filteredMonths.length === 0) {
        // both months are already loaded, so do nothing
        return;
      } else {
        const start = 0;
        let end = 0;
        if (filteredMonths.length === 2) {
          end = 1;
        }

        const startYearMonth = filteredMonths[start];
        const endYearMonth = filteredMonths[end];

        // the startDate will be the 1st day of the first month
        const startDate = DateTime.fromJSDate(
          new Date(startYearMonth.year, startYearMonth.month, 1)
        ).toFormat(yyyy_mm_dd);

        // the endDate will be the last day second month (or the last day of the 1st month if only 1 month will be loaded)
        const endDate = DateTime.fromJSDate(
          new Date(endYearMonth.year, endYearMonth.month, 1)
        )
          .endOf("month")
          .toFormat(yyyy_mm_dd);

        fetchData(isDepartureSelected, startDate, endDate, filteredMonths);
      }
    }

    async function fetchData(
      isDepartureSelected: boolean,
      startDate: string,
      endDate: string,
      filteredMonths: YearMonth[]
    ) {
      if (origin.value && destination.value) {
        const url = getFlightAvailabilityURL(
          isDepartureSelected,
          startDate,
          endDate
        );

        const flightDirection = getFlightDirection(isDepartureSelected);

        const response = await fetcher(
          url,
          null,
          "GET",
          0,
          0,
          FETCH_TIMEOUT.value
        );

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const dates: string[] = response?.dates;
        if (Array.isArray(dates)) {
          populateCache(flightDirection, filteredMonths, dates);
        }

        // show the legend if any of these months have days with no available flights
        hasUnavailableFlights.value = getHasUnavailableFlights(flightDirection);
      }
    }

    function populateCache(
      flightDirection: string,
      filteredMonths: YearMonth[],
      dates: string[]
    ) {
      if (!availableDates.value.get(flightDirection)) {
        // add a Map for the flightDirection (either "origin:destination" or "destination:origin") to the cache
        availableDates.value.set(flightDirection, new Map());
      }
      const monthsData = availableDates.value.get(flightDirection);
      if (monthsData) {
        // add a Map to the cahce for each month that is being loaded
        filteredMonths.forEach((filteredMonth) => {
          const yearMonthStr =
            filteredMonth.year + "-" + zeroPad(filteredMonth.month + 1);
          if (!monthsData.get(yearMonthStr)) {
            // create a Set for the days in this month
            monthsData.set(yearMonthStr, new Set());
          }
        });

        // loop through the dates returned by the server and add them to the cache
        dates.forEach((dateStr) => {
          const yearMonthStr = dateStr.substring(0, 7);
          const daysData = monthsData.get(yearMonthStr);
          if (daysData && isDateStr(dateStr)) {
            const day = dateStr.substring(8, 10);
            // add the day of the month to the Set
            daysData.add(day);
          }
        });
      }
    }

    function getHasUnavailableFlights(flightDirection: string): boolean {
      const flightData = availableDates.value.get(flightDirection);
      if (!flightData) {
        return false;
      }
      for (const [yearMonthStr, monthData] of flightData) {
        const lastDay = DateTime.fromFormat(yearMonthStr + "-01", "yyyy-MM-dd")
          .endOf("month")
          .toJSDate()
          .getDate();

        for (let day = 1; day <= lastDay; day++) {
          const dayStr = zeroPad(day);
          const dateStr = yearMonthStr + "-" + dayStr;

          if (isBeforeMinDate(dateStr)) {
            // skip days before the minimum Date
            // the server can sometimes return days before the minimum date
            continue;
          }

          if (!monthData.has(dayStr)) {
            // return true if we find a date with no flights
            return true;
          }
        }
      }
      return false;
    }

    function isDateStr(dateStr: string) {
      return /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(dateStr);
    }

    function onChangeMonthYear(
      isDepartureSelected: boolean,
      month: number, // month is zero indexed (0 = january)
      year: number
    ) {
      // after clicking the next/previous buttons in the calendar we always need to disply 2 months,
      // the vue3datepicker only provides the "next" month that will be displayed but not which 2 months will be displayed,
      // we can determine which two months it is by which 2 months were previously displayed (displayedMonths.left and displayedMonths.right)
      let directionIsRight = true;

      if (year < displayedMonths.left.year) {
        directionIsRight = false;
      } else if (year === displayedMonths.left.year) {
        if (month < displayedMonths.left.month) {
          directionIsRight = false;
        }
      }

      if (directionIsRight) {
        const yearRight = year;
        const monthRight = month;

        const left = DateTime.fromFormat(
          year + "-" + zeroPad(month + 1) + "-01",
          yyyy_mm_dd
        ).minus({ months: 1 });

        updateDisplayedMonths(
          isDepartureSelected,
          left.get("year"),
          left.get("month") - 1,
          yearRight,
          monthRight,
          true
        );
      } else {
        const yearLeft = year;
        const monthLeft = month;

        const right = DateTime.fromFormat(
          year + "-" + zeroPad(month + 1) + "-01",
          yyyy_mm_dd
        ).plus({ months: 1 });

        updateDisplayedMonths(
          isDepartureSelected,
          yearLeft,
          monthLeft,
          right.get("year"),
          right.get("month") - 1,
          true
        );
      }
    }

    function getFlightAvailabilityURL(
      departureSelected: boolean,
      startDate: string, // yyyy-MM-dd
      endDate: string
    ) {
      if (isBeforeMinDate(startDate) && minDate.value) {
        startDate = minDate.value;
      }

      if (isAfterMaxDate(endDate) && maxDate.value) {
        endDate = maxDate.value;
      }

      const url = new URL(
        VITE_FLIGHT_AVAILABILITY_URL.value +
          "?" +
          qs.stringify({
            origin: departureSelected ? origin.value : destination.value, //"163",
            destination: departureSelected ? destination.value : origin.value, //"2_24_3049111_1375_2478",
            startDate,
            endDate,
            enforceMinConnectionTime: true,
          })
      );
      return url;
    }

    function isBeforeMinDate(dateStr: string) {
      const dateTime = DateTime.fromFormat(dateStr, yyyy_mm_dd)
        .toJSDate()
        .getTime();

      const minDateObj = minDate.value
        ? DateTime.fromFormat(minDate.value, yyyy_mm_dd).toJSDate()
        : undefined;
      if (minDateObj) {
        if (dateTime < minDateObj.getTime()) {
          return true;
        }
      }
      return false;
    }

    function isAfterMaxDate(dateStr: string) {
      const dateTime = DateTime.fromFormat(dateStr, yyyy_mm_dd)
        .toJSDate()
        .getTime();

      const maxDateObj = maxDate.value
        ? DateTime.fromFormat(maxDate.value, yyyy_mm_dd).toJSDate()
        : undefined;
      if (maxDateObj) {
        if (dateTime > maxDateObj.getTime()) {
          // default styling if the date is after the maximum date
          return true;
        }
      }
      return false;
    }

    function getCellClassName(
      dateStr: string, // yyyy-MM-dd
      departureDate: string, // yyyy-MM-dd
      returnDate: string, // yyyy-MM-dd
      departureSelected: boolean,
      flightDirection: string // origin:destination OR destination:origin
    ) {
      if (!isDateStr(dateStr)) {
        return "";
      }

      const yearMonthStr = dateStr.substring(0, 7);

      if (!isMonthLoaded(flightDirection, yearMonthStr)) {
        // default styling if the data for this month is not loaded
        return "";
      }

      if (departureDate) {
        if (dateStr === departureDate) {
          // default styling if this is either the selected departure or return date
          return "";
        }
      }
      if (returnDate) {
        if (dateStr === returnDate) {
          // default styling if this is either the selected departure or return date
          return "";
        }
      }

      if (isBeforeMinDate(dateStr)) {
        // default styling (disabled) if the date is before the minimum date
        return "";
      }

      if (isAfterMaxDate(dateStr)) {
        // default styling (disabled) if the date is after the maximum date
        return "";
      }

      const dateTime = DateTime.fromFormat(dateStr, yyyy_mm_dd)
        .toJSDate()
        .getTime();

      const selectedDepartureDateTime = DateTime.fromFormat(
        departureDate,
        yyyy_mm_dd
      )
        .toJSDate()
        .getTime();

      const selectedReturnDateTime = DateTime.fromFormat(returnDate, yyyy_mm_dd)
        .toJSDate()
        .getTime();

      const hasFlights = dateHasFlightAvailibility(flightDirection, dateStr);
      if (!hasFlights) {
        if (
          departureDate &&
          returnDate &&
          dateTime >= selectedDepartureDateTime &&
          dateTime <= selectedReturnDateTime
        ) {
          // default styling (pink) if date falls inside the selected date range and has flights
          return "";
        } else if (departureSelected) {
          // disabled grey style if this depature date has no departure flights
          return "noflights-cell-departure";
        } else {
          // disabled grey style if this return date has no return flights
          return "noflights-cell-return";
        }
      }

      // this date has available flights and is a valid flight date
      return "";
    }

    return {
      displayedMonths,
      VITE_FLIGHT_AVAILABILITY_URL,
      DEBOUNCE_TIMEOUT,
      availableDates,
      hasUnavailableFlights,
      FETCH_TIMEOUT,
      minDate,
      maxDate,
      getCellClassName,
      isMonthLoaded,
      onChangeMonthYear,
      updateDisplayedMonths,
      reloadAvailabilityData,
      setFlightTrip,
      dateHasFlightAvailibility,
      getFlightAvailabilityURL,
      getFlightDirection,
      getHasUnavailableFlights,
    };
  }
);
